import React from 'react';
import { LONG_INPUT_OPTIONS, PREDICTION_OPTIONS } from './runtimeOptions';
import {
  getTrainedPredictionStrategy,
  isNerTaskType,
  isVllmTaskType,
} from './taskRuntimeDefaults';
import {
  getPredictionStrategyLabel,
  predictionStrategyDisablesParallel,
} from '@/config/runtimeStrategyOptions';
import InsightNotice from './InsightNotice';
import { ChoiceCardGroup, InstancesField, SectionTitle } from './fields';

/** Figma 9952:24753: Serial (list) first, then Parallel (layers). */
const LONG_INPUT_ROWS = [['serial', 'parallel']];
const LONG_INPUT_ICONS = { serial: 'list-details', parallel: 'layers' };
/** Figma 10010:29514: truncation strategies on the first row, aggregation on the second. */
const PREDICTION_ROWS = [
  ['truncate_end', 'truncate_start'],
  ['mean', 'max'],
];

/**
 * Per-task runtime knobs: Number of Instances (optional here — the single-task
 * modal renders it next to File Name), Long Input Processing cards and the
 * Prediction Strategy cards (hidden for NER). Renders nothing for vLLM tasks,
 * which have no Triton instance groups, chunking or aggregation.
 */
export default function TaskRuntimeConfigPanel({
  training,
  runtimeConfig,
  onRuntimeConfigChange,
  instancesCapErrorMessage = null,
  onInstancesFieldChange,
  showInstances = true,
}) {
  if (isVllmTaskType(training)) {
    return null;
  }
  const nerTask = isNerTaskType(training);

  const handleInstancesChange = (value) => {
    onInstancesFieldChange?.();
    onRuntimeConfigChange({ ...runtimeConfig, instance_group_count: value });
  };

  const handleLongInputChange = (value) => {
    onRuntimeConfigChange({ ...runtimeConfig, long_input_processing: value });
  };

  const handlePredictionChange = (value) => {
    onRuntimeConfigChange({ ...runtimeConfig, prediction_strategy: value });
  };

  const truncationSelected = predictionStrategyDisablesParallel(runtimeConfig.prediction_strategy);
  const showLongInputParallelWarning =
    truncationSelected && runtimeConfig.long_input_processing === 'parallel';

  const trainedPredictionStrategy = getTrainedPredictionStrategy(training);
  const selectedPredictionStrategy = runtimeConfig.prediction_strategy;
  const showPredictionStrategyWarning =
    trainedPredictionStrategy &&
    selectedPredictionStrategy &&
    selectedPredictionStrategy !== trainedPredictionStrategy;

  return (
    <>
      {showInstances ? (
        <div className="configure-deployment-modal__fields-row">
          <InstancesField
            value={runtimeConfig.instance_group_count}
            onChange={handleInstancesChange}
            errorMessage={instancesCapErrorMessage}
          />
        </div>
      ) : null}

      <section className="configure-deployment-modal__section" aria-labelledby="configure-deployment-long-input">
        <SectionTitle>
          <span id="configure-deployment-long-input">Long Input Processing</span>
        </SectionTitle>
        <ChoiceCardGroup
          ariaLabel="Long Input Processing"
          options={LONG_INPUT_OPTIONS}
          rows={LONG_INPUT_ROWS}
          icons={LONG_INPUT_ICONS}
          value={runtimeConfig.long_input_processing}
          onChange={handleLongInputChange}
        />
        {showLongInputParallelWarning ? (
          <InsightNotice
            content="Truncation produces a single chunk, so parallel chunk processing does not apply."
            explanation="With a truncation prediction strategy, inputs are processed as one chunk regardless of this setting. Parallel processing only affects models that split long inputs into multiple chunks."
          />
        ) : null}
      </section>

      {nerTask ? null : (
        <section className="configure-deployment-modal__section" aria-labelledby="configure-deployment-prediction">
          <SectionTitle>
            <span id="configure-deployment-prediction">Prediction Strategy</span>
          </SectionTitle>
          <ChoiceCardGroup
            ariaLabel="Prediction Strategy"
            options={PREDICTION_OPTIONS}
            rows={PREDICTION_ROWS}
            value={runtimeConfig.prediction_strategy}
            onChange={handlePredictionChange}
          />
          {showPredictionStrategyWarning ? (
            <InsightNotice
              content={`This model version was trained with '${getPredictionStrategyLabel(trainedPredictionStrategy)}'. You selected '${getPredictionStrategyLabel(selectedPredictionStrategy)}' for deployment.`}
              explanation="Deploying with a different prediction strategy than the one used during training may change how inputs are processed and affect model accuracy."
            />
          ) : null}
        </section>
      )}
    </>
  );
}
